import { Header } from "../../components/layout/header/header";
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { MainLayout } from "../../components/layout/main-layout";
import { Footer } from "../landing-page/footer";
import { ContentContainer } from "../../components/layout/content-container";
import { InfoPagesLayout } from "../../components/layout/info-pages-layout";
import { FormattedMessage } from "react-intl";
import { 
  Business as BusinessIcon,
  Security as SecurityIcon,
  Cookie as CookieIcon,
  Analytics as AnalyticsIcon,
  Gavel as GavelIcon,
  Info as InfoIcon,
  Menu as MenuIcon
} from "@mui/icons-material";

export const ImprintPage = () => {
  const themeContext = useTheme();
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = [
    { id: 'imprint', title: 'Impressum', icon: <BusinessIcon /> },
    { id: 'privacy', title: 'Datenschutzerklärung', icon: <SecurityIcon /> },
    { id: 'data-collection', title: 'Datenerhebung', icon: <InfoIcon /> },
    { id: 'data-usage', title: 'Datennutzung', icon: <AnalyticsIcon /> },
    { id: 'cookies', title: 'Cookies', icon: <CookieIcon /> },
    { id: 'analytics', title: 'Google Analytics', icon: <AnalyticsIcon /> },
    { id: 'rights', title: 'Ihre Rechte', icon: <GavelIcon /> },
    { id: 'liability', title: 'Haftung', icon: <InfoIcon /> }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = window.innerWidth <= 768 ? 120 : 220;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <InfoPagesLayout title={<FormattedMessage id={"footer.imprint"} /> }>
        <PageContainer>
          <TableOfContents>
            <TocTitle>
              <MenuIcon />
              Inhaltsverzeichnis
            </TocTitle>
            <TocList>
              {sections.map((section) => (
                <TocItem 
                  key={section.id}
                  active={activeSection === section.id}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.icon}
                  {section.title}
                </TocItem>
              ))}
            </TocList>
          </TableOfContents>

          <ContentWrapper>
            <Section id="imprint">
              <SectionHeader>
                <BusinessIcon />
                <h2>Impressum</h2>
              </SectionHeader>
              <InfoCard>
                <InfoRow>
                  <InfoLabel>Betreiber:</InfoLabel>
                  <InfoValue>Blagoom GbR</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Vertretungsberechtigte Gesellschafter:</InfoLabel>
                  <InfoValue>Manuel Walzog, David Schmidt und Jonas Gebauer</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Adresse:</InfoLabel>
                  <InfoValue>Werrastraße 13, 12059 Berlin</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>E-Mail:</InfoLabel>
                  <InfoValue>hallo@blagoom.com</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Redaktionell Verantwortliche:</InfoLabel>
                  <InfoValue>Manuel Walzog, David Schmidt und Jonas Gebauer</InfoValue>
                </InfoRow>
              </InfoCard>
              
              <DisclaimerBox>
                <h4>Online-Streitbeilegung</h4>
                <p>
                  Link auf die Plattform der EU-Kommission zur Online-Streitbeilegung: 
                  <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
                    https://ec.europa.eu/consumers/odr
                  </a>
                </p>
                <p>
                  Zur Teilnahme an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle 
                  sind wir nicht verpflichtet und grundsätzlich nicht bereit.
                </p>
                <p>
                  Weitere Informationen unter: 
                  <a href="https://www.abmahnung.de/os-plattform/" target="_blank" rel="noopener noreferrer">
                    www.abmahnung.de/os-plattform/
                  </a>
                </p>
              </DisclaimerBox>
            </Section>

            <Section id="privacy">
              <SectionHeader>
                <SecurityIcon />
                <h2>Datenschutzerklärung</h2>
              </SectionHeader>
              
              <SubSection id="data-collection">
                <h3>1. Schutz und Sicherheit von personenbezogenen Daten</h3>
                <ContentCard>
                  <p>
                    Um die Dienste auf den Webseiten von Blagoom in Anspruch zu nehmen, ist es notwendig, 
                    einige personenbezogene Daten zu erfassen. Blagoom geht mit dem Thema Sicherheit 
                    verantwortungsvoll um und verpflichtet sich deshalb, die gesetzlichen Bestimmungen 
                    zum Schutz personenbezogener Daten unbedingt einzuhalten.
                  </p>
                  <p>
                    In diesem Sinne werden personenbezogene Daten lediglich zur Wahrung berechtigter 
                    eigener Geschäftsinteressen im Hinblick auf die Beratung und Betreuung von Nutzern 
                    erhoben, verarbeitet und genutzt.
                  </p>
                  <SecurityFeatures>
                    <SecurityFeature>
                      <strong>SSL-Verschlüsselung:</strong> Sichere Übertragung aller Nutzerdaten
                    </SecurityFeature>
                    <SecurityFeature>
                      <strong>Kein Verkauf:</strong> Keine Weitergabe an Dritte ohne Zustimmung
                    </SecurityFeature>
                    <SecurityFeature>
                      <strong>Datenschutz:</strong> Einhaltung aller DSGVO-Bestimmungen
                    </SecurityFeature>
                  </SecurityFeatures>
                </ContentCard>
              </SubSection>

              <SubSection id="data-usage">
                <h3>2. Erhebung notwendiger personenbezogener Daten durch Blagoom</h3>
                <ContentCard>
                  <p>
                    Zur Erbringung der Dienstleistung von Blagoom ist es notwendig, dass wir die uns 
                    anvertrauten personenbezogenen Daten unter Beachtung der Datenschutzbedingungen 
                    erheben, speichern, verarbeiten und nutzen.
                  </p>
                  
                  <DataTypesList>
                    <h4>Erhobene Datenarten:</h4>
                    <ul>
                      <li>Name, Vorname, Adresse</li>
                      <li>E-Mail-Adresse, Telefonnummern</li>
                      <li>Geschlecht, Geburtsdatum</li>
                      <li>IP-Adresse und Browser-Informationen</li>
                      <li>Zugriffszeiten und -daten</li>
                    </ul>
                  </DataTypesList>

                  <p>
                    Du kannst die Nutzung Ihrer Daten jederzeit widerrufen bzw. künftigen Verwendungen 
                    Ihrer Daten widersprechen (Art. 21 Abs. 2 DSGVO). Schicke uns hierzu bitte eine 
                    E-Mail an: <a href="mailto:hallo@blagoom.com">hallo@blagoom.com</a>
                  </p>
                </ContentCard>
              </SubSection>

        <SubSection>
                <h3>3. Weitere Datennutzung</h3>
                
                <SubSubSection>
                  <h4>3.1 Verwendung von Facebook Social Plugins</h4>
                  <ContentCard>
                    <p>
                      Der Internetauftritt von Blagoom verwendet Social Plugins des sozialen Netzwerks 
                      facebook.com, das von der Facebook Inc. betrieben wird.
                    </p>
                    <p>
                      <strong>Wichtiger Hinweis:</strong> Wenn Sie nicht möchten, dass Facebook über 
                      unseren Internetauftritt Daten über Sie sammelt, empfehlen wir Ihnen, sich vor 
                      Ihrem Besuch unseres Internetauftritts bei Facebook auszuloggen.
                    </p>
                  </ContentCard>
                </SubSubSection>

                <SubSubSection>
                  <h4>3.2 Verwendung des Social Plugins von Twitter</h4>
                  <ContentCard>
                    <p>
                      Blagoom verwendet Social Plugins des sozialen Netzwerks twitter.com, betrieben 
                      durch die Twitter Inc.
                    </p>
                    <p>
                      <strong>Empfehlung:</strong> Loggen Sie sich vor Ihrem Blagoom-Besuch bei 
                      Twitter aus, wenn Sie keine Datenerfassung wünschen.
                    </p>
                  </ContentCard>
                </SubSubSection>

                <SubSubSection>
                  <h4>3.3 Verwendung von Facebook Connect</h4>
                  <ContentCard>
                    <p>
                      Blagoom bietet die Möglichkeit, sich mit bestehenden Facebook-Profildaten 
                      anzumelden. Bei Nutzung dieser Funktion werden entsprechende Profilinformationen 
                      übermittelt.
                    </p>
                    <p>
                      <strong>Ihre Rechte:</strong> Sie können Ihren Blagoom-Account jederzeit von 
                      Ihrem Facebook-Account trennen. Kontaktieren Sie uns hierfür per E-Mail.
                    </p>
                  </ContentCard>
                </SubSubSection>
        </SubSection>

              <SubSection id="cookies">
                <h3>4. Einsatz von Cookies</h3>
                <ContentCard>
                  <p>
                    Für einen Teil unserer Dienstleistungen ist es erforderlich, dass wir sogenannte 
                    Cookies einsetzen. Ein Cookie ist eine Textdatei, die entweder temporär 
                    ("Sitzungscookie") oder dauerhaft ("permanenter" Cookie) auf der Festplatte 
                    gespeichert wird.
                  </p>
                  
                  <CookieTypes>
                    <h4>Cookie-Arten:</h4>
                    <ul>
                      <li><strong>Sitzungscookies:</strong> Für Login-Authentifizierung und Load-Balancing</li>
                      <li><strong>Marketing-Cookies:</strong> Für Remarketing-Kampagnen und Affiliate-Marketing</li>
                      <li><strong>Funktions-Cookies:</strong> Für nutzerfreundliche Gestaltung</li>
                    </ul>
                  </CookieTypes>

                  <p>
                    <strong>Hinweis:</strong> Sie können Ihren Browser so einstellen, dass Cookies 
                    abgelehnt werden. Beachten Sie jedoch, dass dann nicht alle Angebote störungsfrei 
                    funktionieren.
                  </p>
                </ContentCard>
              </SubSection>

              <SubSection id="analytics">
                <h3>5. Google Analytics</h3>
                <ContentCard>
                  <p>
                    Diese Webseite nutzt den Dienst "Google Analytics" zur Analyse der Websitebenutzung 
                    durch Nutzer. Der Dienst verwendet Cookies, die auf Ihrem Endgerät gespeichert werden.
                  </p>
                  
                  <AnalyticsInfo>
                    <h4>Wichtige Informationen:</h4>
                    <ul>
                      <li>IP-Anonymisierung ist aktiviert</li>
                      <li>Daten werden an Google-Server in den USA übertragen</li>
                      <li>Sie können die Datenerfassung verhindern</li>
                    </ul>
                  </AnalyticsInfo>

                  <p>
                    <strong>Opt-Out:</strong> Sie können die Datenerfassung durch Google Analytics 
                    verhindern, indem Sie das entsprechende Browser-Plugin installieren: 
                    <a href="https://tools.google.com/dlpage/gaoptout?hl=de" target="_blank" rel="noopener noreferrer">
                      Google Analytics Opt-Out
                    </a>
                  </p>
                </ContentCard>
        </SubSection>

              <SubSection id="rights">
                <h3>6. Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung von Daten</h3>
                <ContentCard>
                  <p>
                    Du hast das Recht, die zu Deiner Person gespeicherten persönlichen Daten und 
                    Einstellungen Deines Nutzerkontos auf Blagoom jederzeit über Dein Profil 
                    einzusehen und diese zu modifizieren, zu ergänzen oder zu löschen.
                  </p>
                  
                  <RightsList>
                    <h4>Ihre Rechte:</h4>
                    <ul>
                      <li><strong>Auskunft:</strong> Einsehen Ihrer gespeicherten Daten</li>
                      <li><strong>Berichtigung:</strong> Korrektur falscher Daten</li>
                      <li><strong>Löschung:</strong> Entfernung Ihrer Daten</li>
                      <li><strong>Einschränkung:</strong> Begrenzung der Datenverarbeitung</li>
                    </ul>
                  </RightsList>

                  <p>
                    <strong>Kontakt:</strong> Für alle Anfragen senden Sie bitte eine E-Mail an: 
                    <a href="mailto:hallo@blagoom.com">hallo@blagoom.com</a>
                  </p>
                </ContentCard>
              </SubSection>

              <SubSection id="liability">
                <h3>7. Keine Haftung für Webseiten von Kooperationspartnern</h3>
                <ContentCard>
                  <p>
                    Auf unseren Webseiten verweisen wir auf verschiedene Kooperationspartner, die 
                    ihrerseits Websites und Dienste anbieten. Diese haben in der Regel eigene 
                    Datenschutzerklärungen.
                  </p>
                  <p>
                    <strong>Wichtiger Hinweis:</strong> Diese Datenschutzerklärungen stehen nicht 
                    mit uns in Zusammenhang und wir übernehmen daher keine Haftung. Bitte informieren 
                    Sie sich selbstständig über die jeweiligen Datenschutzerklärungen.
                  </p>
                  <p>
                    Wir behalten uns vor, diese Datenschutzerklärung zu ändern. Die aktuelle Fassung 
                    ist stets unter dieser URL abrufbar.
                  </p>
                </ContentCard>
              </SubSection>
            </Section>
          </ContentWrapper>
        </PageContainer>
      </InfoPagesLayout>
    </>
  );
};

// Styled Components
const PageContainer = styled.div`
  display: flex;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  margin-top: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    margin: 0;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    gap: 0.5rem;
  }
`;

const TableOfContents = styled.div`
  position: sticky;
  top: 220px;
  width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  height: fit-content;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  flex-shrink: 0;

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    width: 100%;
    max-height: none;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 8px;
  }
`;

const TocTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  color: ${props => props.theme.mainTextColor};
  font-size: 1.1rem;
  font-weight: 600;
`;

const TocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  color: ${props => props.active ? props.theme.primaryColor : props.theme.mainTextColor};
  background: ${props => props.active ? props.theme.primary + '20' : 'transparent'};
  border-left: 3px solid ${props => props.active ? props.theme.primaryColor : 'transparent'};

  &:hover {
    background: ${props => props.theme.primary + '10'};
    transform: translateX(4px);
  }

  svg {
    font-size: 1.1rem;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: hidden;
  }
`;

const Section = styled.section`
  margin-bottom: 3rem;
  scroll-margin-top: 220px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    scroll-margin-top: 120px;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.primaryColor};

  h2 {
    margin: 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 2rem;
    font-weight: 600;
  }

  svg {
    font-size: 2rem;
    color: ${props => props.theme.primaryColor};
  }
`;

const SubSection = styled.div`
  margin-bottom: 2rem;

  h3 {
    color: ${props => props.theme.mainTextColor};
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }
`;

const SubSubSection = styled.div`
  margin-bottom: 1.5rem;

  h4 {
    color: ${props => props.theme.mainTextColor};
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  margin-bottom: 0.75rem;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.mainTextColor};
  min-width: 200px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const InfoValue = styled.span`
  color: ${props => props.theme.mainTextColor};
  flex: 1;
`;

const DisclaimerBox = styled.div`
  background: ${props => props.theme.primary + '10'};
  border: 1px solid ${props => props.theme.primaryColor};
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.primaryColor};
    font-size: 1.1rem;
  }

  p {
    margin: 0 0 0.75rem 0;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: ${props => props.theme.primaryColor};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  p {
    line-height: 1.7;
    margin-bottom: 1rem;
    color: ${props => props.theme.mainTextColor};
    word-wrap: break-word;
    overflow-wrap: break-word;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: ${props => props.theme.primaryColor};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SecurityFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1.5rem 0;
`;

const SecurityFeature = styled.div`
  background: ${props => props.theme.primary + '10'};
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid ${props => props.theme.primaryColor};

  strong {
    color: ${props => props.theme.primaryColor};
  }
`;

const DataTypesList = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const CookieTypes = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.75rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};

    strong {
      color: ${props => props.theme.primaryColor};
    }
  }
`;

const AnalyticsInfo = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};
  }
`;

const RightsList = styled.div`
  background: ${props => props.theme.primary + '05'};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;

  h4 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.mainTextColor};
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.75rem;
    line-height: 1.6;
    color: ${props => props.theme.mainTextColor};

    strong {
      color: ${props => props.theme.primaryColor};
    }
  }
`;
